"use client";

import { LucideIcon, Check } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface DomainItem {
  name: string;
  items: string[];
  icon: LucideIcon;
  accent: string;
}

export interface DomainsSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro: string;
  domains: DomainItem[];
  accent: string;
  gridCols?: 2 | 3;
}

export function DomainsSection({
  id = "domains",
  eyebrow,
  title,
  intro,
  domains,
  accent,
  gridCols = 3,
}: DomainsSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            {intro && (
              <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{intro}</p>
            )}
          </div>
        </Reveal>

        <div className={`mt-12 grid gap-4 ${gridCols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {domains.map((d, i) => {
            const Icon = d.icon;
            return (
              <Reveal key={d.name} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-5 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${d.accent} 14%, transparent)`,
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, ${d.accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${d.accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: d.accent }} />
                    </span>
                    <h3 className="text-sm font-bold">{d.name}</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {d.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">
                        <Check className="h-3 w-3 mt-0.5 shrink-0" style={{ color: d.accent }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
